import { Stack } from 'react-bootstrap';
import React, { useEffect, useRef } from 'react';

import Toastify from '../components/Toastify';
import TranslationTextArea from '../components/TranslationTextArea';
import TranslateButton from '../components/TranslateButton';
import TranslateSelection from '../components/TranslateSelection';
import { toastSuccess } from '../util/ToastifyUtil';
import { useDebouncedCallback } from 'use-debounce';
import { Files, XLg } from 'react-bootstrap-icons';
import { useStore } from '../store/store';

const Translate = () => {

	const {
		autoDetectLanguageEnabled,
		auth0TokenId,
		selectedConsumerId,
		textBefore,
		textAfter,
		isTextAfterRtl,
		isTextBeforeRtl,
		selectedSourceLocale,
		autoDetectLang,
		setAutoDetectLanguageEnabled,
		setTextBefore,
		setTextAfter
	} = useStore((state) => state);

	const textBeforeRef = useRef<HTMLTextAreaElement | null>(null);

	// on keyboard input, debounce autodetect with 800ms
	const debouncedAutoDetect = useDebouncedCallback(
		(text: string) => {
			let isAutoDetectEnabled = autoDetectLanguageEnabled && (
				text !== null && text !== '' && text.length > 4 && selectedSourceLocale === -1
			);

			if (isAutoDetectEnabled) {
				autoDetectLang(text, auth0TokenId, selectedConsumerId)
			}
		},
		800
	);

	/**
	 * Focuses text input on page load
	 */
	useEffect(() => {
		textBeforeRef.current?.focus();
	}, []);

	const handlePasteTextBefore = async (event: any) => {
		// this event handler is fired before the text is actually inserted into the input
		// the state: `input.value` = "", if empty (or the 'before paste input.value'), `clipboardData.text` = {text in clipboard}

		// @ts-ignore window.clipboardData
		const clipboardData = event.clipboardData || window.clipboardData;
		const copiedText = clipboardData.getData('text');
		const inputText = event.target.value;

		// if the text to paste is already in the input field, we don't want to trigger detect language
		const isPastedContentAlreadyAppended = inputText.includes(copiedText);

		// if whole input text is selected, we want to always autodetect, since the paste content may be in different language
		const allTextSelectedInInput = event.target.selectionStart == 0 && event.target.selectionEnd == inputText.length;

		if ((autoDetectLanguageEnabled || allTextSelectedInInput) && copiedText && !isPastedContentAlreadyAppended && selectedSourceLocale === -1) {
			if (allTextSelectedInInput) {
				// if paste event replaces input value, only detect based on the paste value
				await autoDetectLang(copiedText, auth0TokenId, selectedConsumerId)
			} else {
				// we want to detect language from the full text input, not just the pasted portion
				const afterPasteInputValue = inputText + copiedText;
				await autoDetectLang(afterPasteInputValue, auth0TokenId, selectedConsumerId)
			}
		}
	};

	const handleClear = () => {
		setTextBefore('');
		textBeforeRef.current?.focus();
		// on clearing field, enabled autodetect again
		setAutoDetectLanguageEnabled(true);
	};

	const handleCopy = async (text: string) => {
		if (text) {
			await navigator.clipboard.writeText(text);
			toastSuccess('Copied to Clipboard.');
		}
	};

	/**
	 * Bump version before deploying app to dev or prod.
	 */
	const appVersion = () => 'v1.4.2'

	return (
		<>
			<Stack direction="vertical" className="p-3 h-100">
				<Toastify />
				<TranslateSelection />
				<Stack className="m-2 overflow-hidden">
					<div className="h-50 overflow-hidden">
						<div className="h-100 overflow-hidden d-block">
							<div className="text-area-container">
								<TranslationTextArea
									rtl={isTextBeforeRtl}
									key={'translateFromInput'}
									id={'translateFromInput'}
									value={textBefore}
									ref={textBeforeRef}
									className="translation-textarea"
									placeholder={
										'Type or paste your text'
									}
									onChange={(event) => {
										const isInputCleared = event.target.value === '';
										// when all text in input is selected and keyboard input happens, we want to enable autodetect,
										// since this behavior is the same as if input was cleared
										const isTextReplaceChangeEvent = event.target.value.length === 1 && textBefore!.length > 4;
										if (isInputCleared || isTextReplaceChangeEvent) {
											setAutoDetectLanguageEnabled(true);
										}
										setTextBefore(event.target.value);
										debouncedAutoDetect(event.target.value);
									}}
									isReadOnly={false}
									onPaste={handlePasteTextBefore}
								/>
								{textBefore &&
									<span
										className="d-inline-block"
										data-toggle="tooltip"
										title="Clear Text">
										<button className="text-area-button" onClick={handleClear}>
											<XLg className="icon" />
										</button>
									</span>
								}
							</div>
						</div>
					</div>
					<hr />
					<div className="h-50 overflow-hidden">
						<div className="h-100 overflow-hidden d-block">
							<div className="text-area-container">
								<TranslationTextArea
									rtl={isTextAfterRtl}
									id={'translateToOutput'}
									key={'translateToOutput'}
									value={textAfter!}
									className="translation-textarea"
									placeholder={''}
									onChange={(event) => {
										setTextAfter(event.target.value);
									}}
									isReadOnly={true}
									tabIndex={-1}
								/>
								{textAfter &&
									<span
										className="d-inline-block"
										data-toggle="tooltip"
										title="Copy Text">
										<button className="text-area-button" onClick={() => handleCopy(textAfter)}>
											<Files className="icon" />
										</button>
									</span>
								}
							</div>
						</div>
					</div>
				</Stack>
				<TranslateButton />
			</Stack>
			<span data-app-version={appVersion()}></span>
		</>
	);
};

export default Translate;
